import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Loader2, Newspaper, ArrowRight } from "lucide-react";
import { Layout } from "@/components/Layout";
import { api, mediaUrl } from "@/lib/api";

const NEWS_FALLBACK =
  "https://images.unsplash.com/photo-1504711434969-e33886168f5c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

function formatDate(d) {
  if (!d) return "";
  return new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

export const NewsCard = ({ article }) => (
  <Link
    to={`/news/${article.slug}`}
    data-testid={`news-card-${article.slug}`}
    className="group flex flex-col bg-white border border-slate-200 rounded-xl overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:border-slate-300"
  >
    <div className="h-44 w-full overflow-hidden bg-slate-100">
      <img
        src={mediaUrl(article.cover_url) || NEWS_FALLBACK}
        alt={article.title}
        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
      />
    </div>
    <div className="p-5 flex flex-col gap-2 flex-1">
      <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-brand">{article.category}</span>
      <h3 className="font-display font-bold text-lg text-slate-900 leading-snug line-clamp-2">{article.title}</h3>
      <p className="text-sm text-slate-500 line-clamp-2">{article.excerpt}</p>
      <div className="mt-auto pt-2 flex items-center justify-between text-xs text-slate-400 font-medium">
        <span>{formatDate(article.created_at)}</span>
        <span className="inline-flex items-center gap-1 text-brand font-semibold group-hover:gap-1.5 transition-all">
          Read <ArrowRight size={13} />
        </span>
      </div>
    </div>
  </Link>
);

export default function NewsList() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cats, setCats] = useState([]);
  const [active, setActive] = useState("all");

  useEffect(() => {
    api.get("/news").then(({ data }) => setArticles(data)).catch(() => setArticles([])).finally(() => setLoading(false));
    api.get("/news/categories").then(({ data }) => setCats(data.categories)).catch(() => {});
  }, []);

  const filtered = active === "all" ? articles : articles.filter((a) => a.category === active);
  const title = "Business News & Insights | IndiaBusinessCircle";
  const desc =
    "Latest GST updates, industry news, growth tips and market trends for Indian businesses — curated by IndiaBusinessCircle.";

  return (
    <Layout>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={desc} />
        <link rel="canonical" href={`${window.location.origin}/news`} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={desc} />
      </Helmet>

      <section className="bg-slate-50 border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-14">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">News & Insights</span>
          <h1 className="mt-3 font-display font-black tracking-tight text-3xl sm:text-4xl text-slate-900">
            Business News & Insights
          </h1>
          <p className="mt-3 text-slate-600 max-w-xl">
            GST updates, industry news and growth tips to help your business stay ahead across India.
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-10">
        {cats.length > 0 && (
          <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2">
            <button
              data-testid="news-cat-all"
              onClick={() => setActive("all")}
              className={`shrink-0 rounded-full px-4 py-1.5 text-sm font-semibold border transition-colors ${active === "all" ? "bg-brand text-white border-brand" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}
            >
              All
            </button>
            {cats.map((c) => (
              <button
                key={c}
                data-testid={`news-cat-${c}`}
                onClick={() => setActive(c)}
                className={`shrink-0 rounded-full px-4 py-1.5 text-sm font-semibold border transition-colors ${active === c ? "bg-brand text-white border-brand" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}
              >
                {c}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="min-h-[30vh] flex items-center justify-center">
            <Loader2 className="animate-spin text-brand" size={32} />
          </div>
        ) : filtered.length === 0 ? (
          <div data-testid="news-empty" className="min-h-[30vh] flex flex-col items-center justify-center text-center">
            <div className="h-16 w-16 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
              <Newspaper className="text-slate-400" size={28} />
            </div>
            <p className="font-display font-bold text-lg text-slate-700">No articles yet</p>
            <p className="text-slate-500 mt-1">Check back soon for the latest business news.</p>
          </div>
        ) : (
          <div data-testid="news-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((a) => (
              <NewsCard key={a.id} article={a} />
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
